import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useImmoTheme } from '../../../lib/immobilien/theme';

/**
 * Rechter Rand der sichtbaren Sidebar (0 ohne Sidebar, z. B. mobil). Fenster des E-Mail-Bereichs legen sich wie im
 * Prototyp über die Inhaltsfläche rechts davon (`inset: 14px 14px 14px <Sidebar + 14>`), die Sidebar bleibt bedienbar.
 */
export function useInhaltsflaecheLinks(): number {
  const lies = () => {
    if (typeof document === 'undefined') return 0;
    const aside = document.querySelector('aside[aria-label="Seitenleiste"]');
    if (!aside) return 0;
    const r = aside.getBoundingClientRect();
    // Als Schublade weggeschoben (translate-x-full) → rechter Rand ≤ 0.
    return r.right > 0 && r.width > 0 ? Math.round(r.right) : 0;
  };
  const [links, setLinks] = useState(lies);
  useEffect(() => {
    const neu = () => setLinks(lies());
    neu();
    window.addEventListener('resize', neu);
    return () => window.removeEventListener('resize', neu);
  }, []);
  return links;
}

/** Handy-Breite (Prototyp `vw < 768`): Fenster füllen dann den ganzen Schirm. */
export function useTelefon(): boolean {
  const [telefon, setTelefon] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const neu = () => setTelefon(window.innerWidth < 768);
    window.addEventListener('resize', neu);
    return () => window.removeEventListener('resize', neu);
  }, []);
  return telefon;
}

/** Abstand der Fenster zum Rand (Prototyp: 14 px). */
export const FENSTER_RAND = 14;

/**
 * Lesefenster des E-Mail-Bereichs (Prototyp: Detail `position: fixed; inset: 14px 14px 14px 262px`, Hintergrund `--bg`):
 * liegt über der Liste und füllt die Inhaltsfläche rechts der Sidebar. Nur diese Fläche wird abgedunkelt und
 * weichgezeichnet; die Sidebar bleibt sichtbar und klickbar. Vollbild deckt alles ab, Handys (< 768 px) ebenfalls.
 * Schließt per Klick daneben oder Esc – Esc verlässt zuerst den Vollbildmodus.
 * Ebene 45: unter der Insel (z-50), damit deren Formulare (z. B. „Kontakt hinzufügen“) darüber erscheinen.
 */
export function GlassModal({ label, vollbild = false, mobil = false, onVollbildAus, onSchliessen, children, ...rest }: {
  label: string;
  vollbild?: boolean;
  /** Mobil (MobileNav sichtbar): unten Platz lassen. */
  mobil?: boolean;
  onVollbildAus?: () => void;
  onSchliessen: () => void;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  const [theme] = useImmoTheme();
  const links = useInhaltsflaecheLinks();
  const telefon = useTelefon();

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Offene Rückfrage, Menüs oder Popups im Fenster schließen zuerst sich selbst.
      if (document.querySelector('[role="alertdialog"], [role="menu"], [data-ueber-modal]')) return;
      if (vollbild && onVollbildAus) onVollbildAus(); else onSchliessen();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [vollbild, onVollbildAus, onSchliessen]);

  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = vorher; };
  }, []);

  const fenster = !vollbild && !telefon;
  // Platz unter dem Inhalt: MobileNav (mobil) bzw. Auslaufzone für den Verlauf am unteren Rand.
  const unten = (mobil ? 104 : 0) + 18;
  let panel: React.CSSProperties;
  if (fenster) {
    // Mobil quer: oben unter der Insel-Pille beginnen – der Kopf ist dort nicht angeheftet, sonst schiebt sich
    // der Inhalt beim Scrollen unter die Pille.
    panel = { top: mobil ? 'max(52px, env(safe-area-inset-top))' : FENSTER_RAND, bottom: FENSTER_RAND, right: FENSTER_RAND, left: links + FENSTER_RAND, borderRadius: 32,
      boxShadow: '0 30px 70px -30px var(--shade), inset 0 0 0 1px var(--line)' };
  } else if (telefon && !vollbild) {
    // Handy: unter der Insel-Pille, unten Platz für die MobileNav.
    panel = { top: 'max(52px, env(safe-area-inset-top))', left: 0, right: 0, bottom: 0, borderRadius: '24px 24px 0 0' };
  } else {
    panel = { inset: 0, borderRadius: 0 };
  }

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: 45, pointerEvents: 'none', fontFamily: "'Manrope', -apple-system, sans-serif", color: 'var(--ink)', colorScheme: theme }}>
      {fenster && (
        <div aria-hidden className="absolute" onMouseDown={onSchliessen}
          style={{ top: 0, right: 0, bottom: 0, left: links, pointerEvents: 'auto', background: 'color-mix(in srgb, var(--bg) 55%, transparent)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }} />
      )}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        {...rest}
        className={`immo-side-in absolute overflow-hidden ${rest.className ?? ''}`}
        style={{ ...panel, pointerEvents: 'auto', background: 'var(--bg)' }}
      >
        {/* Inhalt scrollt in einem inneren Bereich: so läuft er nicht in die runden Ecken und der
            Scrollbalken bleibt aus den Ecken heraus. */}
        <div className="immo-scroll absolute flex flex-col" style={{ inset: fenster ? '6px 4px 6px 0' : 0, overflowY: 'auto', overscrollBehavior: 'contain', paddingBottom: unten }}>
          {children}
        </div>
        {/* Weicher Auslauf statt harter Kante am unteren Rand */}
        <div aria-hidden className="absolute pointer-events-none" style={{ left: 0, right: 0, bottom: 0, height: 28, background: 'linear-gradient(to bottom, color-mix(in srgb, var(--bg) 0%, transparent), var(--bg))' }} />
      </div>
    </div>,
    document.body,
  );
}
